import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLocation, useNavigate } from "react-router-dom";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useMemo } from "react";
import { cva } from "class-variance-authority";
import { DEFAULT_ROUTES } from "@/constants/routes";

interface ScoreDetail {
  criterion: string;
  requested: string;
  offered: string;
  matched: boolean;
}

export const ScoreDetailsModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const scoreDetails = location.state?.scoreDetails as ScoreDetail[];
  const match = Number(location.state?.match);

  const handleClose = () => {
    navigate(DEFAULT_ROUTES.PRIVATE_OFFICE_ADMIN);
  };

  const translateCriterion = (criterion: string) => {
    const translations: Record<string, string> = {
      Capacity: "Capacidade",
      Price: "Preço",
      Proximity: "Proximidade",
      "Required Amenities": "Amenidades obrigatórias",
      "Optional Amenities": "Amenidades opcionais",
    };
    return translations[criterion] || criterion;
  };

  const matchColor = useMemo(() => {
    if (match >= 80) return "green";
    if (match >= 65 && match <= 79) return "yellow";
    return "red";
  }, [match]);

  const variants = cva("rounded-md px-2 py-0.5", {
    variants: {
      color: {
        green: "bg-[#9AE600]",
        yellow: "bg-[#FFB86A]",
        red: "bg-[#FF6467]",
      },
    },
  });

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className="p-6 w-[90vw] max-w-[1000px]">
        <DialogHeader className="mb-6">
          <DialogTitle className="text-xl">
            Como esse score foi calculado?
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Entenda por que essa sala combina (ou não) com o que você procura.
          </p>
        </DialogHeader>

        <div className="space-y-6">
          <div className="border rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="border-b">
                  <th className="px-4 py-3 text-sm font-medium text-left">
                    Critério
                  </th>
                  <th className="px-4 py-3 text-sm font-medium text-left">
                    O que você pediu
                  </th>
                  <th className="px-4 py-3 text-sm font-medium text-left">
                    O que a sala oferece
                  </th>
                  <th className="px-4 py-3 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {scoreDetails?.map((detail, index) => (
                  <tr key={index} className="border-b last:border-b-0">
                    <td className="px-4 py-3 text-sm">
                      {translateCriterion(detail.criterion)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {detail.requested || "Não informou"}
                    </td>
                    <td className="px-4 py-3 text-sm">{detail.offered}</td>
                    <td className="px-4 py-3">
                      <div
                        className={`w-4 h-4 rounded-full ${detail.matched ? "bg-green-500" : "bg-red-500"}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-start gap-2 text-sm text-muted-foreground px-4 py-3 bg-gray-100 rounded-lg border border-gray-200">
            <InfoCircledIcon className="w-6 h-6 text-gray-400" />
            <div>
              <p className="font-medium text-sm text-gray-400">
                Variações no score:
              </p>
              <p className="text-sm text-gray-400">
                O cálculo do score também leva em conta o status da sala, a data
                da última atualização e a distância. Por isso, o percentual pode
                mudar mesmo que os critérios visíveis não sejam alterados.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between border-t pt-4">
            <p className="text-sm font-medium">
              {scoreDetails?.filter((d) => d.matched).length} de{" "}
              {scoreDetails?.length} critérios atendidos
            </p>
            <Badge
              variant="secondary"
              className={`${variants({ color: matchColor })}`}
            >
              Score {match}%
            </Badge>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
